import type { Guid } from 'guid-typescript';
import { getApiHost } from '@/appSettings';
import { RestManager } from '@/newApi/services/rest/RestManager';
import type { DateFormat, Error as SalError } from '@sysarb/sysarb-types-v2/services/v1/import';
import { Role } from '@sysarb/sysarb-types/service-base/enums/role';
import type { useResgate } from '@/login/composables/useResgate';
import { useLoginStore } from '@/login/stores/useLoginStore';
import { useUser } from '@/login/composables/useUser';
import type { InputFieldNames } from '@sysarb/sysarb-types-v2/services/v1/analytics';
import type { ActualVsPredictedEmployment, CopyPayEquityAuditInput, CreateAdjustedSalaryFactorInput, EmployeeImportData, EmploymentDetail, PayEquityAudit, PayFactorStatisticsResponse } from '@sysarb/sysarb-types-v2/services/v1/pay-equity-audit';
import type { ReportData } from '@sysarb/sysarb-types/services/v1/maths-service';

export type EducationLevel = 'none' | 'higher' | 'masters' | 'phd';
export type TargetGroup = 'employee' | 'manager' | 'executive';

export type GenerateAIReportInput = {
    reportLanguage: string;
    reportedYear: number;
    orgCode: string;
    /** Label for the disadvantaged group (e.g., "women", "kvinnor"). Defaults to "women" if blank/missing */
    disadvantagedGroupLabel?: string;
    /** Label for the advantaged group (e.g., "men", "män"). Defaults to "men" if blank/missing */
    advantagedGroupLabel?: string;
    /** Scale factor for word budgets (1=default, 2=double, 3=triple, 4=quadruple). Defaults to 1 */
    wordBudgetScale?: 1 | 2 | 3 | 4;
    /** Education level of the report recipient. Affects language complexity. */
    educationLevel?: EducationLevel;
    /** Target group for the report. Affects focus and terminology. */
    targetGroup?: TargetGroup;
    /** Custom audience prompt that replaces the default audience targeting. If provided, educationLevel and targetGroup are ignored. */
    customAudiencePrompt?: string;
};

export type GenerateAIReportResponse = {
    success: true;
    message: string;
    report: unknown;
    usage?: {
        prompt_tokens: number;
        completion_tokens: number;
        total_tokens: number;
    };
} | {
    success: false;
    message: string;
    error_code?: string;
};

export type GenerateAIReportV2Input = {
    reportLanguage: string;
    reportedYear: number;
    orgCode: string;
    disadvantagedGroupLabel?: string;
    advantagedGroupLabel?: string;
    /** @deprecated Derived server-side from the audit's factor plan; value sent here is ignored. */
    equivalentWorkCriteria?: string[];
    equalWorkDefinition?: string;
    equivalentWorkDefinition?: string;
    employeeInput?: string[];
    analyseInput?: string[];
    summaryExtra?: string;
    actionsExtra?: string;
    previousExtra?: string;
    conclusionExtra?: string;
    preparedBy?: string;
    chosenRepresentative?: string;
    softwareName?: string;
    ourLogoPath?: string;
    theirLogoPath?: string | null;
    urgentThresholdPct?: number;
    /** @deprecated Derived server-side from `payEquityAudit.standardDeviationThreshold`; value sent here is ignored. */
    stdThreshold?: number;
    modelConfidenceDescription?: string;
    /** @deprecated Mistakenly added; not used by the report. Will be removed once all callers stop sending it. */
    leftToReview?: number;
};

export type DuplicatePayEquityAuditInput = CopyPayEquityAuditInput & {
    copySuffix?: string;
};

export type DuplicatePayEquityAuditResponse = {
    id: string;
    message: string;
};

export class PayEquityAuditService {
    private readonly payEquityAuditService = new RestManager(`https://${getApiHost()}/pay-equity-audit-service`);

    public abort(): void {
        this.payEquityAuditService.abort();
    }

    /**
     * Create a new active adjusted pay factor
     *
     * @param {Guid} payEquityAuditId The pay equity audit to create the new factor on
     * @param {CreateAdjustedSalaryFactorInput} data The data for the factor to create
     * @return {*}  {(Promise<PayEquityAudit | null>)}
     * @memberof PayEquityAuditService
     */
    public async createAdjustedSalaryFactor(resgate: ReturnType<typeof useResgate>, payEquityAuditId: Guid, data: CreateAdjustedSalaryFactorInput): Promise<PayEquityAudit | null> {
        const { instanceTopic } = useLoginStore();
        const promise = await resgate.call<PayEquityAudit>(`v1.${instanceTopic}.pay-equity-audit.${payEquityAuditId}.adjusted-salary-factors`, 'create', data).then(res => {
            if (res?.success === false) return null;
            return res.data;
        });

        return promise;
    }

    /**
     * Remove a factor from a pay equity audit
     *
     * @param {Guid} payEquityAuditId The audit to remove from
     * @param {string} adjustedSalaryFactorId the id of the factor to remove
     * @return {*}  {(Promise<PayEquityAudit | null>)}
     * @memberof PayEquityAuditService
     */
    public async removeAdjustedSalaryFactor(resgate: ReturnType<typeof useResgate>, payEquityAuditId: Guid, adjustedSalaryFactorId: string): Promise<PayEquityAudit | null> {
        const { instanceTopic } = useLoginStore();
        const promise = await resgate.call<PayEquityAudit>(`v1.${instanceTopic}.pay-equity-audit.${payEquityAuditId}.adjusted-salary-factor.${adjustedSalaryFactorId}`, 'remove', {}).then(res => {
            if (res?.success === false) return null;
            return res.data;
        });

        return promise;
    }

    /**
     * Get all the data for the template to upload a specific column
     *
     * @param {string} payEquityAuditId The id of the pay equity audit to get the template on
     * @return {Promise<EmployeeImportData[] | null>}
     * @memberof PayEquityAuditService
     */
    public async getEmployeeImportData(payEquityAuditId: string, field: InputFieldNames): Promise<EmployeeImportData[] | null> {
        const { instance } = useLoginStore();
        return this.payEquityAuditService.get(`v1/${instance}/pay-equity-audit/${payEquityAuditId}/employeeImportData?field=${field}`);
    }

    /**
     * Import a template on a specific column the data will be verified
     *
     * @param {string} payEquityAuditId The id of the pay equity audit to import to
     * @param {File} file The file to import
     * @param {InputFieldNames} column The column to import and verify against
     * @param {DateFormat} dateFormat The dateFormat to use if it is a date column
     * @return {*}  {(Promise<{ success: true } | { success: false, error: SalError }>)}
     * @memberof PayEquityAuditService
     */
    public async importEmployeeData(payEquityAuditId: string, file: File, column: InputFieldNames, dateFormat: DateFormat): Promise<{ success: true } | { success: false, error: SalError }> {
        const form = new FormData();
        form.append('file', file, 'file');
        form.append('column', column);
        form.append('dateformat', dateFormat.toString());
        const { instance } = useLoginStore();
        const promise = this.payEquityAuditService.post<{ success: true } | { success: false, error: SalError }>(form, `v1/${instance}/pay-equity-audit/${payEquityAuditId}/importEmployeeData`,
            {
                'x-subdomain': instance ?? '',
                'Content-type': 'multipart/form-data'
            });
        return promise;
    }

    /**
     * Upload final report
     *
     * @param {string} payEquityAuditId The id of the pay equity audit to import to
     * @param {File} file The report to create and upload
     * @return {(Promise<{ success: true } | { success: false, error: SalError }>)}
     * @memberof PayEquityAuditService
     */
    public async uploadFinalReport(payEquityAuditId: string, file: File): Promise<{ success: true, id: string } | { success: false, error: SalError }> {
        const { instance } = useLoginStore();
        const form = new FormData();
        form.append('file', file, file.name);
        const promise = await this.payEquityAuditService.post<{ success: true, id: string } | { success: false, error: SalError }>(form, `v1/${instance}/pay-equity-audit/${payEquityAuditId}/file/create`, { 'x-subdomain': instance ?? '', 'Content-type': 'multipart/form-data' });
        return promise;
    }

    /**
     * Get insight data for the work id
     *
     * @param {string} payEquityAuditId The id of the pay equity audit to get the template on
     * @return {(Promise<EmployeeImportData[] | null>)}
     * @memberof PayEquityAuditService
     */
    public async getInsights(payEquityAuditWorkId: string, language: string, currency: string): Promise<{ insights: string[] } | null> {
        const { instance } = useLoginStore();
        return this.payEquityAuditService.post({ language, currency }, `v1/${instance}/pay-equity-audit-work/${payEquityAuditWorkId}/insights`);
    }

    /**
     * Get actual vs predicted salaries
     *
     * @param {string} payEquityAuditId The id of the pay equity audit to get the template on
     * @return {(Promise<EmployeeImportData[] | null>)}
     * @memberof PayEquityAuditService
     */
    public async getActualVsPredictedSalaries(payEquityAuditId: string, payEquityAuditWorkId: string | null): Promise<ActualVsPredictedEmployment[] | null> {
        const { instanceTopic } = useLoginStore();
        try {
            return await this.payEquityAuditService.get(`v1/${instanceTopic}/pay-equity-audit/${payEquityAuditId}/actual-vs-predicted${payEquityAuditWorkId != null ? `?workId=${payEquityAuditWorkId}` : ''}`);
        } catch (error) {
            console.error('Error getting actual vs predicted salaries', error);
            return null;
        }
    }

    /**
     * Get exact matching employments base on the given employment id's
     * @param {string} payEquityAuditId
     * @param {string[]} employmentIds
     * @returns {Promise<EmploymentDetail[]>}
     */
    public async getExactMatchingEmployments(payEquityAuditId: string, employmentIds: string[]): Promise<EmploymentDetail[]> {
        const { instance, role } = useLoginStore();

        if (role?.role === Role.SalarySurveyAnalyst) {
            return [];
        }

        const promise = await this.payEquityAuditService.post<EmploymentDetail[]>({ employmentIds }, `v1/${instance}/pay-equity-audit/${payEquityAuditId}/employments-exact-match`, { 'x-subdomain': instance ?? '', });
        return promise;
    }

    /**
     * Duplicate a Pay Equity Audit (Mapping) and all its related entities.
     * Creates a complete copy with new UUIDs while preserving external references.
     *
     * @param {string} payEquityAuditId The id of the pay equity audit to duplicate
     * @param {DuplicatePayEquityAuditInput} input The copy settings and optional suffix
     * @returns {Promise<DuplicatePayEquityAuditResponse>} The id of the newly created duplicate
     */
    public async duplicatePayEquityAudit(payEquityAuditId: string, input: DuplicatePayEquityAuditInput): Promise<DuplicatePayEquityAuditResponse> {
        const { instance } = useLoginStore();
        return this.payEquityAuditService.post<DuplicatePayEquityAuditResponse>(
            input,
            `v1/${instance}/duplicate-pea/${payEquityAuditId}`,
            { 'x-subdomain': instance ?? '' }
        );
    }

    /**
     * Fetch pay factor statistics for a given pay equity audit.
     *
     * @param {Guid} payEquityAuditId The pay equity audit to create the new factor on
     */
    public async getAdjustedSalaryFactorStatistics(resgate: ReturnType<typeof useResgate>, payEquityAuditId: Guid): Promise<PayFactorStatisticsResponse | null> {
        const { instanceTopic } = useLoginStore();
        const promise = await resgate.call<PayFactorStatisticsResponse>(`v1.${instanceTopic}.pay-equity-audit.${payEquityAuditId}.adjusted-salary-factors`, 'getstatistics', {}).then(res => {
            if (res?.success === false) return null;
            return res.data;
        });

        return promise;
    }

    /**
     * Fetch pay factor statistics for a given pay equity audit draft.
     *
     * @param {ReturnType<typeof useResgate>} resgate The resgate instance
     * @param {Guid} payEquityAuditDraftId The pay equity audit draft ID
     * @return {Promise<PayFactorStatisticsResponse | null>}
     * @memberof PayEquityAuditService
     */
    public async getAdjustedSalaryFactorStatisticsForDraft(resgate: ReturnType<typeof useResgate>, payEquityAuditDraftId: string): Promise<PayFactorStatisticsResponse | null> {
        const { instanceTopic } = useLoginStore();
        const { role, loginUserId } = useUser();
        const promise = await resgate.call<PayFactorStatisticsResponse>(`v1.${instanceTopic}.${loginUserId.value}.${role.value?.role}.pay-equity-audit-draft.${payEquityAuditDraftId}`, 'getstatistics', {}).then(res => {
            if (res.success === false) return null;
            return res.data;
        });

        return promise;
    }

    /**
     * Add employments to a pay equity audit mapping.
     *
     * @param {string} peaId The pay equity audit ID
     * @param {string[]} employmentIds The employment UUIDs to add
     */
    public async addEmployments(peaId: string, employmentIds: string[]): Promise<void> {
        const { instance } = useLoginStore();
        await this.payEquityAuditService.post<void>(
            { employmentIds },
            `v1/${instance}/pay-equity-audit/${peaId}/add-employments`,
            { 'x-subdomain': instance ?? '' }
        );
    }

    /**
     * Generate an AI-powered pay equity report.
     * Calls the pay-equity-audit-service which runs statistical analyses and uses GPT
     * to generate a structured pay equity report.
     *
     * @param {string} payEquityAuditId The id of the pay equity audit
     * @param {GenerateAIReportInput} input The input parameters for report generation
     * @returns {Promise<GenerateAIReportResponse>} The generated report or error
     */
    public async generateAIReport(payEquityAuditId: string, input: GenerateAIReportInput): Promise<GenerateAIReportResponse> {
        const { instance } = useLoginStore();
        return this.payEquityAuditService.post<GenerateAIReportResponse>(
            input,
            `v1/${instance}/pay-equity-audit/${payEquityAuditId}/generate-ai-report`,
            { 'x-subdomain': instance ?? '' }
        );
    }

    /**
     * Generate an AI-powered pay equity report (v2).
     * Fetches Blinder-Oaxaca, actual-vs-predicted, cost-to-fix, and employment data,
     * then assembles a fully-populated ReportData via the AI report builder.
     *
     * @param {string} payEquityAuditId The id of the pay equity audit
     * @param {GenerateAIReportV2Input} input The input parameters for report generation
     * @returns {Promise<ReportData>} The fully-populated report data
     */
    public async generateAIReportV2(payEquityAuditId: string, input: GenerateAIReportV2Input): Promise<ReportData> {
        const { instance } = useLoginStore();
        return this.payEquityAuditService.post<ReportData>(
            input,
            `v1/${instance}/pay-equity-audit/${payEquityAuditId}/generate-ai-reportv2`,
            { 'x-subdomain': instance ?? '' }
        );
    }
}
